/**
 * Agent commission — shared types (Phase 1a).
 *
 * Mirror of the persistence-side enums on AgentPayout. Lives in
 * @evi/shared so consumers in either tier (admin, web, api) can
 * reference the same string set without importing across the
 * @evi/db dependency edge. The DB model re-declares the type
 * locally for the Mongoose enum array literal (Mongoose enums need
 * runtime arrays, not type-only imports).
 *
 * Phase 1a ships the types + schema foundations. Phase 2 wires the
 * resolver (Tenant.admin_fee_bps + Charger.agent_id snapshotting
 * onto Session). Phase 3 wires the Transfer call that flips
 * AgentPayout.status from pending → transferred. Phase 4 ships the
 * retry cron. Phase 5 ships the admin UI that consumes
 * AgentSummary in the agent-list view.
 *
 * Phase 1b ships the RBAC additions (sales_agent template +
 * AgentPayout resource) separately — explicitly NOT included here.
 */
import { z } from 'zod';
export const AgentPayoutStatusSchema = z.enum([
    // ── Phase 1a baseline lifecycle ──────────────────────────────────
    'pending',
    'transferred',
    'failed',
    'reversed',
    'absorbed_clawback',
    // ── Phase 3a additions (no runtime consumer yet) ─────────────────
    // Pre-transfer gate: AgentPayout row is created at session-stop but
    // the Stripe Transfer can't fire because the agent hasn't completed
    // Stripe Connect onboarding (Agent.stripe_connect_account_id is null).
    // The retry cron in Phase 3d will pick these up automatically when
    // the agent's Connect lands; they transition to 'pending' (queued for
    // the next Transfer attempt) once the account is ready.
    'pending_stripe_setup',
    // Terminal state after MAX_RETRIES exhaustion. Mirrors the audit
    // semantic of `payout.resident_transfer.given_up` in the resident
    // retry cron (apps/api/src/jobs/retryFailedResidentTransfers.ts).
    // Surfaces in the admin "stuck commissions" queue for manual ops
    // resolution; the retry cron stops scanning rows in this state.
    'permanently_failed',
    // Refund handling intermediate state: the underlying session refunded
    // but transfers.createReversal hasn't fired yet (queued / in-flight).
    // Distinguishes the post-refund-pre-reversal window from the terminal
    // 'reversed' (success) and 'absorbed_clawback' (reversal failed,
    // debt landed on Agent.outstanding_clawback_cents) states. Phase 3e
    // refund handler writes this on charge.refunded receipt; flips to
    // 'reversed' or 'absorbed_clawback' once the reversal call settles.
    'clawback_pending',
    // Terminal, DELIBERATE non-payment: the session started BEFORE the SP
    // reached qualification (Agent.qualified_at null, or session.started_at
    // < qualified_at). Earnings activate at the SP's 4th deployed pay-to-use
    // charger; sessions predating that pay no SP share, with no retro
    // re-rating. NOT a failure — the retry cron (scans status='failed')
    // never touches these, and they do NOT surface in the stuck-commissions
    // queue. The row exists purely as an audit trail of foregone commission.
    'skipped_pre_qualification',
    // Terminal, DELIBERATE non-payment under the SP origination rule
    // (Warren, 2026-09-30): the session's site is residential, EVI-sourced or
    // has no recorded partner origination. An older row (written before the
    // reservation workflow) is moved here so the retry job stops re-reading it.
    'skipped_ineligible_site',
]);
export const RefundHandlingSchema = z.enum([
    'reversed',
    'absorbed_clawback',
]);
//# sourceMappingURL=agent-commission.js.map